// =============================================================================
// ResourceGrid - Componente principale dello scheduler con Drag & Drop
// =============================================================================

import { useMemo, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { DndContext, DragOverlay, rectIntersection } from '@dnd-kit/core';
import { RefreshCw, Filter, Plus, Timer, Clock, LayoutGrid, List, CalendarRange } from 'lucide-react';
import { cn } from '../../lib/utils';
import type { BoundedViewMode } from '../../store/schedulerStore';
import {
  useCalendarView,
  useViewMode,
  useSelectedDate,
  useSelectedResourceTypes,
  useSelectedResourceIds,
  useResourceSearchTerm,
  useSchedulerActions,
  useSidebarOpen,
  useShowBufferZones,
  useGridTimeRange,
  useIsModalOpen,
  usePreviewAppointment,
  useBookingPanelMode,
} from '../../store/schedulerStore';
import { useResources, useResourceTypes, useAppointments, useAppointmentsRange, useUpcomingAppointments, useMoveAppointment, useServices, useDayExceptions } from '../../api/hooks';
import type { AvailabilityException } from '../../utils/availabilityUtils';
import { buildGridHours, calcTotalGridMinutes, getAppointmentStyle } from './utils/grid.utils';
import { civilRangeFor, formatCivilRangeLabel, shiftCivilAnchor } from './utils/agenda.utils';
import { useDateLocale } from '../../hooks/useDateLocale';
import { useTenantTimeZone } from '../../store/tenantStore';
import { civilDayOf, civilDayToCarrier } from '../../lib/tenantTime';
import { TimeZoneNotice } from './components/TimeZoneNotice';
import { useDragAndDrop } from './hooks/useDragAndDrop';
import { DateNavigator } from './components/DateNavigator';
import { ServiceLegend } from './components/ServiceLegend';
import { TimeGridHeader } from './components/TimeGridHeader';
import { ResourceSidebar } from './components/ResourceSidebar';
import { FilterPresetMenu } from './components/FilterPresetMenu';
import { DroppableResourceRow } from './components/DroppableResourceRow';
import { StaticAppointmentBlock } from './components/StaticAppointmentBlock';
import { ExceptionLegend } from './components/ExceptionLegend';
import { AgendaView } from './components/AgendaView';
import { BookingPanel } from './BookingModal';
import { ROW_HEIGHT } from './utils/grid.constants';
import { parseISO, isSameDay } from 'date-fns';

export function ResourceGrid() {
  const { t } = useTranslation('scheduler');
  const dateLocale = useDateLocale();
  // Store
  const selectedDate = useSelectedDate();
  const calendarView = useCalendarView();
  const agendaRange = useViewMode();
  const selectedResourceTypes = useSelectedResourceTypes();
  const selectedResourceIds = useSelectedResourceIds();
  const resourceSearchTerm = useResourceSearchTerm();
  const sidebarOpen = useSidebarOpen();
  const showBufferZones = useShowBufferZones();
  const isBookingPanelOpen = useIsModalOpen();
  const previewAppointment = usePreviewAppointment();
  const bookingPanelMode = useBookingPanelMode();
  const { gridStartHour, gridEndHour, customStartHour, customEndHour, useCustomTimeRange } = useGridTimeRange();
  const { 
    goToNextDay, goToPrevDay, goToToday, setDate,
    toggleResourceType, setResourceTypes, toggleResource, setResources, setSearchTerm, resetFilters,
    toggleSidebar, toggleBufferZones, setGridTimeRange, toggleCustomTimeRange,
    openModal, openEditModal, setCalendarView, setViewMode,
  } = useSchedulerActions();

  const isAgenda = calendarView === 'agenda';
  // Elenco aperto da oggi in poi: niente confini, niente navigazione per data.
  const isUpcoming = isAgenda && agendaRange === 'upcoming';

  // Intervallo dell'agenda: la data selezionata e' solo il riferimento, la
  // navigazione si muove di un periodo intero (giorno / settimana / mese).
  const anchorCivilDay = civilDayOf(selectedDate);
  const boundedRange: BoundedViewMode = agendaRange === 'upcoming' ? 'day' : agendaRange;
  const agendaCivilRange = useMemo(
    () => civilRangeFor(anchorCivilDay, boundedRange),
    [anchorCivilDay, boundedRange],
  );

  // Griglia dinamica
  const hours = useMemo(() => buildGridHours(gridStartHour, gridEndHour), [gridStartHour, gridEndHour]);
  const totalGridMinutes = useMemo(() => calcTotalGridMinutes(gridStartHour, gridEndHour), [gridStartHour, gridEndHour]);
  
  // Toast locale per feedback drag
  const [toast, setToast] = useState<{ message: string; type: 'error' | 'success' } | null>(null);
  
  // Data fetching
  const { data: resourceTypes = [], isLoading: typesLoading } = useResourceTypes();
  const { data: resources = [], isLoading: resourcesLoading } = useResources();
  const { data: services = [] } = useServices();
  // Le tre modalita' leggono dati diversi: solo quella attiva va in rete.
  const {
    data: dayAppointments = [],
    isLoading: dayAppointmentsLoading,
    refetch: refetchDayAppointments,
  } = useAppointments(selectedDate, undefined, undefined, { enabled: !isAgenda });
  const {
    data: rangeAppointments = [],
    isLoading: rangeLoading,
    refetch: refetchRangeAppointments,
  } = useAppointmentsRange(agendaCivilRange.start, agendaCivilRange.end, {
    enabled: isAgenda && !isUpcoming,
  });
  const upcoming = useUpcomingAppointments({ enabled: isUpcoming });
  const { data: dayExceptions = [] } = useDayExceptions(selectedDate);

  // Le pagine gia' scaricate sono un unico elenco: il raggruppamento per
  // giornata e i filtri lavorano su quello.
  const upcomingAppointments = useMemo(
    () => upcoming.data?.pages.flatMap((page) => page.data) ?? [],
    [upcoming.data],
  );
  const upcomingTotal = upcoming.data?.pages[0]?.total ?? 0;

  const agendaAppointments = isUpcoming ? upcomingAppointments : rangeAppointments;
  const agendaLoading = isUpcoming ? upcoming.isLoading : rangeLoading;

  const appointments = isAgenda ? agendaAppointments : dayAppointments;
  const appointmentsLoading = isAgenda ? agendaLoading : dayAppointmentsLoading;
  const refetchAppointments = isAgenda
    ? isUpcoming
      ? upcoming.refetch
      : refetchRangeAppointments
    : refetchDayAppointments;

  // Raggruppa le eccezioni del giorno per risorsa per il calendario
  const exceptionsByResource = useMemo(() => {
    const map = new Map<string, AvailabilityException[]>();
    for (const exc of dayExceptions) {
      const list = map.get(exc.resourceId) ?? [];
      list.push(exc);
      map.set(exc.resourceId, list);
    }
    return map;
  }, [dayExceptions]);

  const moveAppointment = useMoveAppointment();
  
  // Drag & Drop
  const { activeId, draggedAppointment, draggedRect, sensors, handleDragStart, handleDragEnd } = useDragAndDrop({
    appointments,
    gridStartHour,
    gridEndHour,
    moveAppointment,
    refetchAppointments,
    onToast: (t) => {
      setToast(t);
      if (t.type === 'success') setTimeout(() => setToast(null), 2000);
    },
  });
  
  // Filtra risorse per tipo, selezione individuale e termine di ricerca
  const filteredResources = useMemo(() => {
    return resources.filter((r) => {
      const typeMatch = selectedResourceTypes === null || selectedResourceTypes.includes(r.resourceTypeId);
      const resourceMatch = selectedResourceIds.length === 0 || selectedResourceIds.includes(r.id);
      const searchMatch = !resourceSearchTerm || r.name.toLowerCase().includes(resourceSearchTerm.toLowerCase());
      return typeMatch && resourceMatch && searchMatch;
    });
  }, [resources, selectedResourceTypes, selectedResourceIds, resourceSearchTerm]);
  
  const isLoading = typesLoading || resourcesLoading || appointmentsLoading;

  // In agenda i filtri della sidebar restano validi: senza filtri attivi si
  // mostra tutto, altrimenti solo gli appuntamenti che toccano queste risorse.
  const isResourceFilterActive =
    selectedResourceTypes !== null || selectedResourceIds.length > 0 || !!resourceSearchTerm;

  // Quanti criteri di filtro sono attivi: e' il numero mostrato sul bottone
  // della toolbar, cosi' i filtri restano visibili anche a sidebar chiusa.
  const activeFilterCount =
    (selectedResourceTypes !== null ? 1 : 0) +
    (selectedResourceIds.length > 0 ? 1 : 0) +
    (resourceSearchTerm ? 1 : 0);
  const agendaResourceFilterIds = useMemo(
    () => (isResourceFilterActive ? filteredResources.map((r) => r.id) : null),
    [isResourceFilterActive, filteredResources],
  );

  // In agenda prev/next scorrono di un periodo intero, non di un giorno.
  const goToPrevPeriod = useCallback(() => {
    setDate(civilDayToCarrier(shiftCivilAnchor(anchorCivilDay, boundedRange, -1)));
  }, [setDate, anchorCivilDay, boundedRange]);

  const goToNextPeriod = useCallback(() => {
    setDate(civilDayToCarrier(shiftCivilAnchor(anchorCivilDay, boundedRange, 1)));
  }, [setDate, anchorCivilDay, boundedRange]);

  const handleSlotClick = (start: Date, resourceId: string, resourceName: string) => {
    openModal({ start, resourceId, resourceName });
  };
  
  const handleAppointmentClick = useCallback((appointmentId: string) => {
    openEditModal(appointmentId);
  }, [openEditModal]);

  return (
    <DndContext 
      sensors={sensors}
      collisionDetection={rectIntersection}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <div className="h-full flex flex-row">
        {/* ── Grid Area (shrinks when booking panel is open) ── */}
        <div className={cn('flex-1 flex flex-col bg-white min-w-0 transition-all duration-300')}>
        {/* Header */}
        <header className="flex flex-col gap-3 px-6 py-4 border-b border-gray-200 bg-white">
          {/* Compare solo se l'operatore sta guardando da un altro fuso */}
          <TimeZoneNotice />
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3">
              {/* Nell'elenco aperto non c'e' una data da navigare */}
              {isUpcoming ? (
                <div className="flex items-center gap-2 text-lg font-semibold text-gray-900">
                  <CalendarRange className="w-5 h-5 text-gray-400" />
                  {t('agenda.upcomingTitle')}
                </div>
              ) : (
                <DateNavigator
                  date={selectedDate}
                  label={isAgenda ? formatCivilRangeLabel(agendaCivilRange, boundedRange, dateLocale) : undefined}
                  onDateChange={setDate}
                  onPrev={isAgenda ? goToPrevPeriod : goToPrevDay}
                  onNext={isAgenda ? goToNextPeriod : goToNextDay}
                  onToday={goToToday}
                />
              )}

              {/* Griglia risorse vs elenco cronologico */}
              <div className="flex items-center gap-0.5 p-0.5 rounded-lg bg-gray-100">
                <button
                  onClick={() => setCalendarView('grid')}
                  title={t('view.gridTitle')}
                  aria-pressed={!isAgenda}
                  className={cn(
                    'flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-sm font-medium transition-colors',
                    !isAgenda ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                  )}
                >
                  <LayoutGrid className="w-4 h-4" />
                  {t('view.grid')}
                </button>
                <button
                  onClick={() => setCalendarView('agenda')}
                  title={t('view.agendaTitle')}
                  aria-pressed={isAgenda}
                  className={cn(
                    'flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-sm font-medium transition-colors',
                    isAgenda ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                  )}
                >
                  <List className="w-4 h-4" />
                  {t('view.agenda')}
                </button>
              </div>

              {/* Ampiezza dell'intervallo: solo in agenda */}
              {isAgenda && (
                <div className="flex items-center gap-0.5 p-0.5 rounded-lg border border-gray-200">
                  {(['day', 'week', 'month', 'upcoming'] as const).map((mode) => (
                    <button
                      key={mode}
                      onClick={() => setViewMode(mode)}
                      aria-pressed={agendaRange === mode}
                      className={cn(
                        'px-2.5 py-1 rounded-md text-sm font-medium transition-colors',
                        agendaRange === mode
                          ? 'bg-emerald-100 text-emerald-700'
                          : 'text-gray-500 hover:bg-gray-100'
                      )}
                    >
                      {t(`agenda.range.${mode}`)}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="flex items-center gap-4 flex-wrap">
              <button
                onClick={() => refetchAppointments()}
                disabled={isLoading}
                className={cn('p-2 rounded-lg hover:bg-gray-100 transition-colors', isLoading && 'animate-spin')}
                aria-label={t('refresh')}
              >
                <RefreshCw className="w-5 h-5 text-gray-600" />
              </button>
              
              <button
                onClick={toggleSidebar}
                aria-pressed={sidebarOpen}
                title={sidebarOpen ? t('sidebar.hide') : t('sidebar.show')}
                className={cn(
                  'flex items-center gap-2 pl-3 pr-3 py-2 rounded-lg border text-sm font-medium transition-colors',
                  sidebarOpen
                    ? 'border-emerald-200 bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
                    : 'border-gray-200 text-gray-700 hover:bg-gray-100'
                )}
              >
                <Filter className="w-4 h-4" />
                <span>{t('sidebar.button')}</span>
                {activeFilterCount > 0 && (
                  <span className="min-w-[1.25rem] px-1.5 py-0.5 rounded-full bg-emerald-600 text-white text-xs font-semibold tabular-nums leading-none flex items-center justify-center">
                    {activeFilterCount}
                  </span>
                )}
              </button>

              <FilterPresetMenu />

              {/* Buffer e fascia oraria hanno senso solo sulla griglia */}
              {!isAgenda && (
                <>
              <button
                onClick={toggleBufferZones}
                className={cn('p-2 rounded-lg transition-colors', showBufferZones ? 'bg-amber-100 text-amber-700' : 'hover:bg-gray-100')}
                title={showBufferZones ? t('hideBuffers') : t('showBuffers')}
              >
                <Timer className="w-5 h-5" />
              </button>

              {/* Selettore fascia oraria */}
              <div className="flex items-center gap-1.5 border border-gray-200 rounded-lg px-2 py-1">
                <Clock className="w-4 h-4 text-gray-400" />
                {useCustomTimeRange ? (
                  <>
                    <select
                      value={customStartHour}
                      onChange={(e) => {
                        const newStart = Number(e.target.value);
                        if (newStart < customEndHour) setGridTimeRange(newStart, customEndHour);
                      }}
                      className="text-sm bg-transparent border-none focus:ring-0 p-0 pr-5 cursor-pointer text-gray-700 font-medium"
                    >
                      {Array.from({ length: 24 }, (_, i) => (
                        <option key={i} value={i} disabled={i >= customEndHour}>
                          {String(i).padStart(2, '0')}:00
                        </option>
                      ))}
                    </select>
                    <span className="text-gray-400 text-xs">—</span>
                    <select
                      value={customEndHour}
                      onChange={(e) => {
                        const newEnd = Number(e.target.value);
                        if (newEnd > customStartHour) setGridTimeRange(customStartHour, newEnd);
                      }}
                      className="text-sm bg-transparent border-none focus:ring-0 p-0 pr-5 cursor-pointer text-gray-700 font-medium"
                    >
                      {Array.from({ length: 24 }, (_, i) => i + 1).map((h) => (
                        <option key={h} value={h} disabled={h <= customStartHour}>
                          {String(h).padStart(2, '0')}:00
                        </option>
                      ))}
                    </select>
                  </>
                ) : (
                  <span className="text-sm text-gray-500 font-medium px-1">{t('fullDayRange')}</span>
                )}
                <button
                  onClick={toggleCustomTimeRange}
                  className={cn(
                    'ml-1 px-2 py-0.5 text-xs rounded-md transition-colors font-medium',
                    useCustomTimeRange
                      ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  )}
                  title={useCustomTimeRange ? t('fullDayTitle') : t('customRangeTitle')}
                >
                  {useCustomTimeRange ? t('timeRange') : t('fullDay')}
                </button>
              </div>
                </>
              )}

              <button
                onClick={() => openModal()}
                className="flex items-center gap-2 px-4 py-2 btn-primary font-medium shadow-sm"
              >
                <Plus className="w-5 h-5" />
                {t('newAppointment')}
              </button>
            </div>
          </div>
          
          <ServiceLegend services={services} appointments={appointments} />
        </header>
        
        {/* Contenuto principale */}
        <div className="flex-1 flex overflow-hidden relative">
          <ResourceSidebar
            resources={resources}
            resourceTypes={resourceTypes}
            selectedTypes={selectedResourceTypes}
            selectedResourceIds={selectedResourceIds}
            searchTerm={resourceSearchTerm}
            onToggleType={toggleResourceType}
            onSetTypes={setResourceTypes}
            onToggleResource={toggleResource}
            onSetResources={setResources}
            onSearchChange={setSearchTerm}
            onResetFilters={resetFilters}
            isOpen={sidebarOpen}
            onToggleSidebar={toggleSidebar}
          />
          
          <main className="flex-1 flex flex-col overflow-hidden">
            {isLoading && (
              <div className="absolute inset-0 bg-white/50 flex items-center justify-center z-20">
                <div className="flex items-center gap-2 text-gray-600">
                  <RefreshCw className="w-5 h-5 animate-spin" />
                  <span>{t('common:status.loading')}</span>
                </div>
              </div>
            )}
            
            {isAgenda ? (
              <AgendaView
                appointments={appointments}
                resourceFilterIds={agendaResourceFilterIds}
                variant={isUpcoming ? 'upcoming' : 'range'}
                pagination={
                  isUpcoming
                    ? {
                        total: upcomingTotal,
                        loaded: upcomingAppointments.length,
                        hasMore: !!upcoming.hasNextPage,
                        isLoadingMore: upcoming.isFetchingNextPage,
                        onLoadMore: () => upcoming.fetchNextPage(),
                      }
                    : undefined
                }
                onAppointmentClick={handleAppointmentClick}
                onNewAppointment={() => openModal()}
              />
            ) : (
              <>
            <TimeGridHeader hours={hours} />

            <div className="flex-1 overflow-auto">
              {filteredResources.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-gray-500 gap-3">
                  <Filter className="w-12 h-12 text-gray-300" />
                  <div className="text-center">
                    <p className="font-medium">
                      {resources.length === 0 
                        ? t('noResources')
                        : t('noResourcesFiltered')
                      }
                    </p>
                    {resources.length > 0 && (
                      <button
                        onClick={resetFilters}
                        className="mt-2 text-sm text-emerald-600 hover:text-emerald-800"
                      >
                        {t('resetFilters')}
                      </button>
                    )}
                  </div>
                </div>
              ) : (
                filteredResources.map((resource) => (
                  <div key={resource.id} className="relative">
                    <DroppableResourceRow
                      resource={resource}
                      appointments={appointments}
                      exceptions={exceptionsByResource.get(resource.id) ?? []}
                      hours={hours}
                      dayStart={selectedDate}
                      services={services}
                      showBufferZones={showBufferZones}
                      gridStartHour={gridStartHour}
                      gridEndHour={gridEndHour}
                      totalGridMinutes={totalGridMinutes}
                      onSlotClick={handleSlotClick}
                      onAppointmentClick={handleAppointmentClick}
                    />
                    {/* Ghost appointment preview (solo in drawer mode) */}
                    {bookingPanelMode === 'drawer' && previewAppointment &&
                      isSameDay(parseISO(previewAppointment.scheduledStart), selectedDate) &&
                      previewAppointment.allocations.some(a => a.resourceId === resource.id) && (
                        <GhostAppointmentBlock
                          previewAppointment={previewAppointment}
                          gridStartHour={gridStartHour}
                          totalGridMinutes={totalGridMinutes}
                        />
                      )}
                  </div>
                ))
              )}
            </div>
            
            <footer className="px-4 py-2 bg-gray-50 border-t border-gray-200 text-sm text-gray-600 flex items-center justify-between gap-4 flex-wrap">
              <span>
                <span className="font-medium">{appointments.length}</span> {t('appointmentsFor')}{' '}
                <span className="font-medium">{filteredResources.length}</span> {t('resourcesCount')}
                {moveAppointment.isPending && (
                  <span className="ml-4 text-emerald-600">
                    <RefreshCw className="w-4 h-4 inline animate-spin mr-1" />
                    {t('moving')}
                  </span>
                )}
              </span>
              <ExceptionLegend exceptions={dayExceptions} />
            </footer>
              </>
            )}
          </main>
        </div>
        
        <DragOverlay dropAnimation={null}>
          {activeId && draggedAppointment ? (
            <StaticAppointmentBlock
              appointment={draggedAppointment}
              rect={draggedRect || undefined}
            />
          ) : null}
        </DragOverlay>
        
        {/* Toast notifiche */}
        {toast && (
          <div className={cn(
            'fixed bottom-4 right-4 z-50 flex items-center gap-3 px-4 py-3 rounded-lg shadow-lg',
            'animate-in slide-in-from-right duration-300',
            toast.type === 'error' ? 'bg-red-500 text-white' : 'bg-green-500 text-white'
          )}>
            <span className="text-sm font-medium">{toast.message}</span>
            <button onClick={() => setToast(null)} className="p-1 hover:bg-white/20 rounded">
              ✕
            </button>
          </div>
        )}
      </div>
      {/* ── End of Grid Area ── */}

      {/* ── Booking Side Panel (solo in modalità drawer) ── */}
      {isBookingPanelOpen && bookingPanelMode === 'drawer' && (
        <div className="w-[540px] flex-shrink-0 h-full animate-in slide-in-from-right duration-300">
          <BookingPanel />
        </div>
      )}
      </div>
    </DndContext>
  );
}

// ===========================================================================
// Ghost Appointment Preview Block (rendered on the grid)
// ===========================================================================

interface GhostAppointmentBlockProps {
  previewAppointment: NonNullable<ReturnType<typeof usePreviewAppointment>>;
  gridStartHour: number;
  totalGridMinutes: number;
}

function GhostAppointmentBlock({
  previewAppointment,
  gridStartHour,
  totalGridMinutes,
}: GhostAppointmentBlockProps) {
  const timeZone = useTenantTimeZone();
  const selectedDate = useSelectedDate();
  const style = getAppointmentStyle(
    previewAppointment.scheduledStart,
    previewAppointment.scheduledEnd,
    gridStartHour,
    totalGridMinutes,
    civilDayOf(selectedDate),
    timeZone,
  );

  const bgColor = previewAppointment.serviceColor || '#10b981';
  const leftPct = parseFloat(style.left);
  const widthPct = parseFloat(style.width);

  return (
    <div
      className="absolute top-0 pointer-events-none z-10"
      style={{
        /* 192px = w-48 (resource name column) */
        left: `calc(192px + (100% - 192px) * ${leftPct / 100})`,
        width: `calc((100% - 192px) * ${widthPct / 100})`,
        height: `${ROW_HEIGHT}px`,
        padding: '4px 0',
      }}
    >
      <div
        className="h-full rounded-md border-2 border-dashed flex items-center px-2 gap-1.5 animate-pulse-slow"
        style={{
          backgroundColor: bgColor + '25',
          borderColor: bgColor,
        }}
      >
        <div
          className="w-2 h-2 rounded-full flex-shrink-0"
          style={{ backgroundColor: bgColor }}
        />
        <div className="truncate text-xs font-medium" style={{ color: bgColor }}>
          {previewAppointment.serviceName}
          {previewAppointment.customerName && ` · ${previewAppointment.customerName}`}
        </div>
      </div>
    </div>
  );
}

export default ResourceGrid;